import React, { useState, useEffect } from 'react';
import { FaUserCircle, FaSave, FaKey, FaIdBadge } from 'react-icons/fa';
import api, { errorMessage, saveSession, getUser } from '../api';
import { ROLE_LABELS } from './attendance/roles';
import {
  PageShell, Card, Badge, Button, Field, inputClass, Alert, Loading, formatDate,
} from './attendance/ui';

/** The signed-in user's own record: editable contact details and password. */
const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [form, setForm] = useState({});
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    api.get('auth/me/')
      .then(({ data }) => {
        setIdentity(data);
        setProfile(data.profile);
        setForm({
          phone: data.profile?.phone || '',
          address: data.profile?.address || '',
          gender: data.profile?.gender || '',
          dateOfBirth: data.profile?.dateOfBirth || '',
          emergencyContactName: data.profile?.emergencyContactName || '',
          emergencyContactPhone: data.profile?.emergencyContactPhone || '',
        });
      })
      .catch((err) => setError(errorMessage(err, 'Could not load your profile.')))
      .finally(() => setLoading(false));
  }, []);

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.patch('auth/me/', form);
      setProfile(data);
      setNotice('Your details have been updated.');
    } catch (err) {
      setError(errorMessage(err, 'Could not save your details.'));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (passwords.newPassword !== passwords.confirm) {
      setError('The new password and its confirmation do not match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('auth/change-password/', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      // The old token was revoked; keep the session alive with the new one.
      saveSession({ token: data.token, user: getUser() });
      setPasswords({ currentPassword: '', newPassword: '', confirm: '' });
      setNotice('Your password has been changed.');
    } catch (err) {
      setError(errorMessage(err, 'Could not change your password.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageShell title="My Profile"><Loading /></PageShell>;

  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  return (
    <PageShell title="My Profile" subtitle="Your staff record and account security">
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <div className="flex flex-col items-center text-center">
            <FaUserCircle className="text-7xl text-gray-300 mb-3" />
            <h2 className="text-xl font-bold text-gray-800">{profile?.name || identity?.username}</h2>
            <p className="text-gray-500 text-sm">{profile?.position || '—'}</p>
            <div className="mt-2"><Badge value={profile?.status || 'active'} /></div>
          </div>
          <dl className="mt-6 space-y-3 text-sm">
            {[
              ['Staff ID', profile?.employeeId],
              ['Role', ROLE_LABELS[identity?.role] || identity?.role],
              ['Department', profile?.department],
              ['Location', profile?.location],
              ['Shift', profile?.shift],
              ['Employment', profile?.employmentType?.replace('_', ' ')],
              ['Hire date', profile?.hireDate ? formatDate(profile.hireDate, { day: 'numeric', month: 'short', year: 'numeric' }) : null],
              ['Biometric ID', profile?.biometricId],
              ['Username', identity?.username],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 border-b border-gray-50 pb-2">
                <dt className="text-gray-500">{label}</dt>
                <dd className="font-semibold text-gray-800 text-right capitalize">{value || '—'}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <Card title={<span className="flex items-center gap-2"><FaIdBadge className="text-gray-400" /> Contact details</span>}>
            <Alert tone="info">
              Assignment details — department, location, shift and role — are maintained by HR.
            </Alert>
            <form onSubmit={saveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Phone">
                <input value={form.phone} onChange={update('phone')} className={inputClass} />
              </Field>
              <Field label="Date of birth">
                <input type="date" value={form.dateOfBirth} onChange={update('dateOfBirth')} className={inputClass} />
              </Field>
              <Field label="Gender">
                <select value={form.gender} onChange={update('gender')} className={inputClass}>
                  <option value="">Not specified</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Address">
                <input value={form.address} onChange={update('address')} className={inputClass} />
              </Field>
              <Field label="Emergency contact">
                <input value={form.emergencyContactName} onChange={update('emergencyContactName')} className={inputClass} />
              </Field>
              <Field label="Emergency phone">
                <input value={form.emergencyContactPhone} onChange={update('emergencyContactPhone')} className={inputClass} />
              </Field>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={saving}>
                  <FaSave /> {saving ? 'Saving…' : 'Save details'}
                </Button>
              </div>
            </form>
          </Card>

          <Card title={<span className="flex items-center gap-2"><FaKey className="text-gray-400" /> Change password</span>}>
            <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Current password">
                <input type="password" required value={passwords.currentPassword} className={inputClass}
                  onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })} />
              </Field>
              <Field label="New password" hint="At least 8 characters.">
                <input type="password" required minLength="8" value={passwords.newPassword} className={inputClass}
                  onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })} />
              </Field>
              <Field label="Confirm new password">
                <input type="password" required value={passwords.confirm} className={inputClass}
                  onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} />
              </Field>
              <div className="sm:col-span-3">
                <Button type="submit" disabled={saving}>
                  <FaKey /> {saving ? 'Updating…' : 'Change password'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </PageShell>
  );
};

export default MyProfile;
